import { FC, useMemo } from 'react';
import { CardWorkflow } from 'src/presentations/components/molecules/card/CardWorkflow';
import { CardTheme } from 'src/presentations/components/molecules/card/cardAnalytic/types';
import { WorkflowDto, WorkflowRuleDto } from 'src/generated/services/TFinancialApi';

// Import icon images
import iconWorkflow from 'src/assets/images/icons/hierarchy-3.svg';
import iconDocument from 'src/assets/images/icons/document-code-3.svg';
import iconComponent from 'src/assets/images/icons/component.svg';

interface AuditWorkflowStatsProps {
  workflow: WorkflowDto | undefined;
  workflowRules: WorkflowRuleDto[];
}

export const AuditWorkflowStats: FC<AuditWorkflowStatsProps> = (props): JSX.Element => {
  const { workflow, workflowRules } = props;

  // Count active rules for this workflow
  const activeRulesCount = useMemo(() => {
    if (!workflowRules) return 0;
    return workflowRules.filter((rule) => rule.enabled !== false).length;
  }, [workflowRules]);

  // Get billed entity from rules (since WorkflowDto doesn't have this field)
  const billedEntity = useMemo(() => {
    if (!workflowRules || workflowRules.length === 0) return 'N/A';
    // Get the type from the first rule (assuming all rules for a workflow have same type)
    const firstRule = workflowRules[0];
    return firstRule?.type || 'N/A';
  }, [workflowRules]);

  const workflowStats = [
    {
      label: 'Workflow Name',
      value: workflow?.name || workflow?.description || 'Loading...',
      icon: iconWorkflow,
      theme: CardTheme.BLUE_LIGHT,
    },
    {
      label: 'Billed Entity',
      value: billedEntity,
      icon: iconComponent,
      theme: CardTheme.BLUE,
    },
    {
      label: 'Active Rules',
      value: activeRulesCount.toString(),
      icon: iconDocument,
      theme: CardTheme.PURPLE,
    },
  ];

  return (
    <div className='py-20 xl:py-24 px-16 xl:px-24 border-b border-neutral-20 bg-neutral-10'>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 xl:gap-16'>
        {workflowStats.map((stat) => (
          <CardWorkflow key={stat.label} label={stat.label} value={stat.value} icon={stat.icon} theme={stat.theme} />
        ))}
      </div>
    </div>
  );
};
